import { AppRoutes } from '../common/constants';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Link,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { JSX, useState } from 'react';
import { makeSignedS3Url } from '../common/helpers/makeSignedS3Url';
import {
  Report,
  REPORT_SECTION_FILE_LABELS,
  REPORT_STATUS_PROPERTY_LABEL,
  REPORT_ZIP_FILE_LABEL,
  ReportFile,
} from '../common/types/reports';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import Content from '../common/components/content';
import Layout from '../common/components/layout';
import LayoutLoadingState from '../common/components/layout/layoutLoadingState';

export enum ReportStatus {
  InProgress = 'in-progress',
  Completed = 'completed',
  Failed = 'failed',
}

type ReportStatusData = Record<string, ReportStatus> & {
  status: ReportStatus;
  buildVersion: string;
};

const SectionDownloadButton = ({
  report,
  sectionName,
}: {
  sectionName: string;
  report: Report;
}): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  /**
   * Get section files by section name
   * @param sectionName Section name to get files
   * @returns Section files
   */
  const getSectionFiles = (): ReportFile[] => {
    const section = report.sections.find((section) => section.sectionName === sectionName);
    return (section?.files || []).filter(({ fileLabel }) =>
      REPORT_SECTION_FILE_LABELS.includes(fileLabel),
    );
  };

  return (
    <>
      <Button
        onClick={({ currentTarget }): void => setAnchorEl(currentTarget)}
        color="secondary"
        variant="contained"
        size="small"
      >
        Download
      </Button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={(): void => setAnchorEl(null)}>
        {getSectionFiles().map((file) => (
          <MenuItem
            key={file.id}
            onClick={(): void => {
              window.open(makeSignedS3Url(file.fileUrl), '_blank');
            }}
          >
            {file.fileUploadName}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * Page with report generation results
 */
const ReportResults = (): JSX.Element | null => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { data: report } = useQuery<Report>({
    queryKey: [`/ReportSummary/${id}`],
    // Polling interval for in-progress reports
    refetchInterval: ({ state: { data } }) => {
      const statusProperty = data?.properties.find(
        ({ propertyLabel }) => propertyLabel === REPORT_STATUS_PROPERTY_LABEL,
      );
      if (!statusProperty) return 10_000;
      const statusData: ReportStatusData = JSON.parse(statusProperty.propertyValue);
      if (statusData.status === ReportStatus.InProgress) return 10_000;
      return false;
    },
  });

  if (!report) {
    return <LayoutLoadingState />;
  }

  const statusProperty = report.properties.find(
    ({ propertyLabel }) => propertyLabel === REPORT_STATUS_PROPERTY_LABEL,
  );

  if (!statusProperty) {
    navigate(AppRoutes.Reports);
    return null;
  }

  const statusData: ReportStatusData = JSON.parse(statusProperty.propertyValue);
  const sections = Object.entries(statusData).filter(
    ([key]) => key !== 'status' && key !== 'buildVersion',
  );

  /**
   * Open report zip file when user clicks on "Done" button
   */
  const handleClickDownload = (): void => {
    const file = report.files.find(({ fileLabel }) => fileLabel === REPORT_ZIP_FILE_LABEL);
    if (!file) {
      enqueueSnackbar({
        message: 'Can`t find destination file in the report data',
        variant: 'error',
      });
      return;
    }
    window.open(makeSignedS3Url(file.fileUrl), '_blank');
  };

  const breadcrumbs = [
    { label: 'Reports', href: AppRoutes.Reports },
    { label: 'Report Builder', href: `${AppRoutes.Reports}/${report.id}` },
  ];

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <Content>
        <Typography variant="h2">
          {statusData.status === ReportStatus.InProgress && 'Your reportpack is being built'}
          {statusData.status === ReportStatus.Completed && 'Your report is ready!'}
          {statusData.status === ReportStatus.Failed && 'Something went wrong'}
        </Typography>
        <Box className="my-4">
          <Typography color="primary.main">
            We are generating your documents. Keep this page open to check the progress.
          </Typography>
        </Box>
        <Card>
          <CardContent>
            <Box>
              {sections.map(([key, status]) => (
                <Box
                  key={key}
                  className="md:flex md:justify-between items-center py-4 hover:bg-gray-100 rounded"
                >
                  <Box className="flex items-center space-x-4">
                    <Avatar sx={{ bgcolor: 'background.default', color: 'text.secondary' }}>
                      <Icon width={20} icon="gis:layer-alt-o" />
                    </Avatar>
                    <Typography variant="subtitle1" fontWeight={800}>
                      {key}
                    </Typography>
                  </Box>
                  <Box
                    width={220}
                    className="flex items-center md:justify-end space-x-2 mt-4 md:mt-0"
                  >
                    {status === ReportStatus.Completed && (
                      <SectionDownloadButton sectionName={key} report={report} />
                    )}
                    {status === ReportStatus.InProgress && <LinearProgress className="flex-grow" />}
                    {status === ReportStatus.Failed && (
                      <LinearProgress
                        className="flex-grow"
                        value={1}
                        variant="determinate"
                        color="error"
                      />
                    )}
                    {status === ReportStatus.Failed && (
                      <Icon icon="material-symbols:exclamation" width={24} />
                    )}
                  </Box>
                </Box>
              ))}
              {statusData.status === ReportStatus.Completed && (
                <Box className="mt-8 text-center space-x-2">
                  <Button
                    onClick={handleClickDownload}
                    variant="contained"
                    color="secondary"
                    size="large"
                  >
                    Download ZIP
                  </Button>
                  <Button component={Link} href={AppRoutes.Reports} variant="text" size="large">
                    Back to reports
                  </Button>
                </Box>
              )}
              <Box className="mt-12 text-center">
                <Typography variant="caption" color="text.secondary">
                  Build Rev.: {statusData.buildVersion}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Content>
    </Layout>
  );
};
export default ReportResults;
